import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import {
  createTest,
  deleteTest,
  getTestBySlug,
  queryTests,
  updateTest,
} from '@/features/tests/data';
import {
  gridPaths,
  invalidateGridQuery,
  invalidateTestDetail,
  invalidateTests,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
} from '@/shared/hooks';
import { removeFromResult, upsertIntoResult } from '@/shared/hooks/shared';
import type { ApiError } from '@/shared/data/client';
import type { QueryResult, TestDetailDto, TestListItemDto } from '@flowqa/shared';

const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

export const useTestsQuery = (
  projectId?: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<QueryResult<TestListItemDto>, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const isEnabled = Boolean(token) && (options?.enabled ?? true);
  return useQuery<QueryResult<TestListItemDto>, ApiError>({
    queryKey: queryKeys.tests(projectId, orgKey),
    enabled: isEnabled,
    queryFn: async (): Promise<QueryResult<TestListItemDto>> =>
      await queryTests(projectId, orgForQuery, {
        page: 1,
        pageSize: 50,
        sorts: [{ field: 'createdAt', direction: 'desc' }],
        filters: [],
        include: { features: true, suites: { ids: true, primary: true } },
      }),
    staleTime: 30_000,
  });
};

export const useTestDetailQuery = (
  slug: string | null,
  projectId?: string | null,
): UseQueryResult<TestDetailDto | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  return useQuery<TestDetailDto | null, ApiError>({
    queryKey: queryKeys.testDetail(slug, orgKey, effectiveProjectId),
    enabled: Boolean(token && slug),
    queryFn: async (): Promise<TestDetailDto | null> =>
      await getTestBySlug(slug, effectiveProjectId, selectedOrganisationId ?? undefined),
    staleTime: 15_000,
  });
};

const toTestListItem = (test: TestDetailDto): TestListItemDto => ({
  id: test.id,
  slug: test.slug,
  name: test.name,
  organisationId: test.organisationId,
  projectId: test.projectId,
  project: test.project,
  features: test.features,
  suites: test.suites,
  latestExecution: test.latestExecution,
  deletedAt: test.deletedAt,
});

export const useCreateTest = (): UseMutationResult<
  TestDetailDto,
  ApiError,
  {
    name: string;
    projectId: string;
    featureIds?: string[];
    suiteIds?: string[];
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.tests(undefined, orgKey);
  const projectKey = (projectId: string): ReturnType<typeof queryKeys.tests> =>
    queryKeys.tests(projectId, orgKey);

  return useMutation<
    TestDetailDto,
    ApiError,
    {
      name: string;
      projectId: string;
      featureIds?: string[];
      suiteIds?: string[];
    },
    {
      previousAll?: QueryResult<TestListItemDto>;
      previousProject?: QueryResult<TestListItemDto>;
      projectId: string;
      tempId: string;
    }
  >({
    mutationFn: async (input) => await createTest(input, selectedOrganisationId),
    onMutate: (input) => {
      const tempId = `temp-${Date.now()}`;
      const optimistic: TestListItemDto = {
        id: tempId,
        slug: toSlug(input.name),
        name: input.name.trim(),
        organisationId: selectedOrganisationId ?? null,
        projectId: input.projectId,
        features: input.featureIds ? { ids: input.featureIds } : { ids: [] },
        suites: input.suiteIds ? { ids: input.suiteIds } : { ids: [] },
        deletedAt: null,
      };
      const previousAll = queryClient.getQueryData<QueryResult<TestListItemDto>>(allKey);
      const previousProject = queryClient.getQueryData<QueryResult<TestListItemDto>>(
        projectKey(input.projectId),
      );
      queryClient.setQueryData<QueryResult<TestListItemDto>>(allKey, (prev) =>
        upsertIntoResult(prev, optimistic),
      );
      queryClient.setQueryData<QueryResult<TestListItemDto>>(projectKey(input.projectId), (prev) =>
        upsertIntoResult(prev, optimistic),
      );
      return { previousAll, previousProject, projectId: input.projectId, tempId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(projectKey(context.projectId), context.previousProject);
      }
    },
    onSuccess: async (created, _vars, context) => {
      const createdListItem = toTestListItem(created);
      if (context.tempId) {
        queryClient.setQueryData<QueryResult<TestListItemDto>>(allKey, (prev) =>
          upsertIntoResult(removeFromResult(prev, context.tempId), createdListItem),
        );
        queryClient.setQueryData<QueryResult<TestListItemDto>>(
          projectKey(created.projectId),
          (prev) => upsertIntoResult(removeFromResult(prev, context.tempId), createdListItem),
        );
      }
      await invalidateQueries([
        invalidateTests(undefined, orgKey),
        invalidateTestDetail(null, orgKey, created.projectId),
        invalidateGridQuery(gridPaths.tests),
      ]);
    },
  });
};

export const useUpdateTest = (): UseMutationResult<
  TestDetailDto,
  ApiError,
  { id: string; name?: string; featureIds?: string[]; suiteIds?: string[]; tags?: string[] },
  {
    previousAll?: QueryResult<TestListItemDto>;
    previousProject?: QueryResult<TestListItemDto>;
    projectId?: string;
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.tests(undefined, orgKey);
  const projectKey = (projectId: string | undefined): ReturnType<typeof queryKeys.tests> =>
    queryKeys.tests(projectId ?? null, orgKey);

  return useMutation<
    TestDetailDto,
    ApiError,
    { id: string; name?: string; featureIds?: string[]; suiteIds?: string[]; tags?: string[] },
    {
      previousAll?: QueryResult<TestListItemDto>;
      previousProject?: QueryResult<TestListItemDto>;
      projectId?: string;
    }
  >({
    mutationFn: async (input) => await updateTest(input, selectedOrganisationId),
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<TestListItemDto>>(allKey);
      const projectId = previousAll?.items.find((item) => item.id === input.id)?.projectId;
      const previousProject = projectId
        ? queryClient.getQueryData<QueryResult<TestListItemDto>>(projectKey(projectId))
        : undefined;
      queryClient.setQueryData<QueryResult<TestListItemDto>>(allKey, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item.id === input.id
                  ? {
                      ...item,
                      ...(input.name ? { name: input.name } : {}),
                      ...(input.featureIds ? { features: { ids: input.featureIds } } : {}),
                      ...(input.suiteIds
                        ? { suites: { ...(item.suites ?? {}), ids: input.suiteIds } }
                        : {}),
                      ...(input.tags ? { tags: input.tags } : {}),
                    }
                  : item,
              ),
            }
          : prev,
      );
      if (projectId) {
        queryClient.setQueryData<QueryResult<TestListItemDto>>(projectKey(projectId), (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((item) =>
                  item.id === input.id
                    ? {
                        ...item,
                        ...(input.name ? { name: input.name } : {}),
                        ...(input.featureIds ? { features: { ids: input.featureIds } } : {}),
                        ...(input.suiteIds
                          ? { suites: { ...(item.suites ?? {}), ids: input.suiteIds } }
                          : {}),
                        ...(input.tags ? { tags: input.tags } : {}),
                      }
                    : item,
                ),
              }
            : prev,
        );
      }
      return { previousAll, previousProject, projectId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject && context.projectId) {
        queryClient.setQueryData(projectKey(context.projectId), context.previousProject);
      }
    },
    onSuccess: async (updated) => {
      await invalidateQueries([
        invalidateTests(undefined, orgKey),
        invalidateTests(updated.projectId, orgKey),
        invalidateTestDetail(updated.slug, orgKey, updated.projectId),
        invalidateGridQuery(gridPaths.tests),
      ]);
    },
  });
};

export const useDeleteTest = (): UseMutationResult<
  { id: string },
  ApiError,
  { id: string; projectId: string },
  { previousAll?: QueryResult<TestListItemDto>; previousProject?: QueryResult<TestListItemDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.tests(undefined, orgKey);
  const projectKey = (projectId: string): ReturnType<typeof queryKeys.tests> =>
    queryKeys.tests(projectId, orgKey);

  return useMutation<
    { id: string },
    ApiError,
    { id: string; projectId: string },
    { previousAll?: QueryResult<TestListItemDto>; previousProject?: QueryResult<TestListItemDto> }
  >({
    mutationFn: async (input) => await deleteTest({ id: input.id }, selectedOrganisationId),
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<TestListItemDto>>(allKey);
      const previousProject = queryClient.getQueryData<QueryResult<TestListItemDto>>(
        projectKey(input.projectId),
      );
      queryClient.setQueryData<QueryResult<TestListItemDto>>(allKey, (prev) =>
        removeFromResult(prev, input.id),
      );
      queryClient.setQueryData<QueryResult<TestListItemDto>>(projectKey(input.projectId), (prev) =>
        removeFromResult(prev, input.id),
      );
      return { previousAll, previousProject };
    },
    onError: (_err, input, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(projectKey(input.projectId), context.previousProject);
      }
    },
    onSuccess: async (_deleted, input) => {
      await invalidateQueries([
        invalidateTests(undefined, orgKey),
        invalidateTests(input.projectId, orgKey),
        invalidateTestDetail(null, orgKey, input.projectId),
        invalidateGridQuery(gridPaths.tests),
      ]);
    },
  });
};

export const useInvalidateTests = (): ((projectId?: string | null) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateTests(projectId, orgKey),
        invalidateGridQuery(gridPaths.tests),
      ]);
    },
    [invalidateQueries, orgKey],
  );
};

export const useInvalidateTestDetail = (): ((
  slug: string | null,
  projectId?: string | null,
) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (slug: string | null, projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateTestDetail(slug, orgKey, projectId ?? selectedProjectId ?? null),
      ]);
    },
    [invalidateQueries, orgKey, selectedProjectId],
  );
};
